import { Icon } from '@iconify/react'
import { useEffect, useMemo, useState } from 'react'
import { getContributions, getData } from '../api'
import type { Contribution, Item } from '../types'
import { ExportPanel } from '../components/ExportPanel'

const baseColumns: Array<keyof Item> = [
  'source',
  'external_id',
  'url',
  'scraped_at',
  'scraped_by_user_id',
  'owner_user_id',
  'dedupe_key',
]

function renderBase(item: Item, col: keyof Item) {
  const val = item[col]
  if (col === 'url' && typeof val === 'string') {
    return (
      <a className="text-cyan-400 underline" href={val} target="_blank" rel="noreferrer">
        {val}
      </a>
    )
  }
  if (col === 'scraped_at' && typeof val === 'string') {
    const dt = new Date(val)
    return Number.isNaN(dt.valueOf()) ? val : dt.toLocaleString()
  }
  // if (typeof val === 'number') return val.toString()
  if (typeof val === 'string') return val
  if (val == null) return '-'
  return JSON.stringify(val)
}

function renderAttr(val: unknown) {
  if (val == null) return '-'
  if (typeof val === 'string') return val
  if (typeof val === 'number' || typeof val === 'boolean') return String(val)
  return JSON.stringify(val)
}

type Props = {
  viewerUserId: string
}

export function DataPanel({ viewerUserId }: Props) {
  const [items, setItems] = useState<Item[]>([])
  const [contributions, setContributions] = useState<Contribution[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const load = async () => {
    setLoading(true)
    setError('')
    try {
      const [itemRows, contribRows] = await Promise.all([getData(200), getContributions()])
      setItems(itemRows)
      setContributions(contribRows)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'load failed')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  const attributeKeys = useMemo(() => {
    const keys = new Set<string>()
    for (const row of items) {
      if (!row.attributes) continue
      for (const key of Object.keys(row.attributes)) keys.add(key)
    }
    return [...keys].sort()
  }, [items])

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon icon="mdi:database" width="22" height="22" className="text-cyan-400" />
          <h1 className="text-xl font-semibold">Ingested data</h1>
        </div>
        <button className="btn" onClick={() => void load()}>Refresh</button>
      </div>

      {loading && <div className="panel">Loading...</div>}
      {!loading && error && <div className="panel text-rose-300">Error: {error}</div>}

      {!loading && !error && (
        <>
          <section className="panel mb-6">
            <h2 className="mb-4 text-lg font-semibold">Contribution share</h2>
            {contributions.length === 0 ? (
              <p className="text-slate-300">No data yet.</p>
            ) : (
              <ul className="space-y-2">
                {contributions.map((row) => {
                  const viewerRow = row.user_id === viewerUserId
                  return (
                    <li key={row.user_id} className={`rounded-md p-3 ${viewerRow ? 'viewer-highlight bg-amber-950/60' : 'bg-slate-800'}`}>
                      <div className="mb-1 flex justify-between text-sm">
                        <span className={viewerRow ? 'font-semibold text-amber-300' : ''}>{row.user_id}</span>
                        <span>{row.percentage.toFixed(1)}%</span>
                      </div>
                      <div className="h-2 rounded bg-slate-700">
                        <div className={`h-2 rounded ${viewerRow ? 'bg-amber-400' : 'bg-cyan-500'}`} style={{ width: `${Math.max(2, row.percentage)}%` }} />
                      </div>
                      <div className="mt-1 text-xs text-slate-400">{row.unique_count} owned rows</div>
                    </li>
                  )
                })}
              </ul>
            )}
          </section>

          <section className="panel mb-6 overflow-x-auto">
            <h2 className="mb-4 text-lg font-semibold">All rows</h2>
            <table className="w-full min-w-[1200px] border-collapse text-sm">
              <thead>
                <tr className="border-b border-slate-700 text-left">
                  {baseColumns.map((col) => (
                    <th key={col} className="px-2 py-2 text-xs uppercase text-slate-400">{col}</th>
                  ))}
                  {attributeKeys.map((k) => (
                    <th key={k} className="px-2 py-2 text-xs uppercase text-slate-400">attr.{k}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const viewerRow = item.owner_user_id === viewerUserId || item.scraped_by_user_id === viewerUserId
                  return (
                    <tr key={item.dedupe_key} className={`border-b border-slate-800 align-top ${viewerRow ? 'bg-amber-950/30' : ''}`}>
                      {baseColumns.map((col) => (
                        <td key={col} className={`px-2 py-2 ${viewerRow ? 'text-amber-100' : 'text-slate-100'}`}>{renderBase(item, col)}</td>
                      ))}
                      {attributeKeys.map((k) => (
                        <td key={k} className={`px-2 py-2 ${viewerRow ? 'text-amber-200' : 'text-slate-300'}`}>{renderAttr(item.attributes?.[k])}</td>
                      ))}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </section>

          {/* Export section */}
          <section className="panel">
            <h2 className="mb-4 text-lg font-semibold">Export</h2>
            {(items.length > 0) ? (
              <ExportPanel
                data={{ results: items }}
                initialEngine="text"
                fileName="scraped-data.txt"
              />
            ) : (
              <p className="text-sm text-slate-400">No data to export yet.</p>
            )}
          </section>
        </>
      )}
    </div>
  )
}
